import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Tabs } from 'antd'
import styled from 'styled-components'
import { Layout } from '@surecompanies/core_components'
import Page from 'layouts/Page'
import PageWrapper from 'enrollment-platform/PageWrapper'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import { FINANCE_PATH } from '../../constants'
import { VcnManagementBenefitTab } from './VcnManagementBenefitTab'
import { VcnManagementCardTab } from './VcnManagementCardTab'
import { VcnManagementJobProgressModal } from '../../components/VcnManagement/VcnManagementJobProgressModal'
import { useVcnBulkJobCurrent } from '../../components/VcnManagement/hooks/useVcnBulkJobCurrent'
import { useInvalidateVcnData } from '../../components/VcnManagement/hooks/useInvalidateVcnData'
import { VcnBulkJobState } from '../../api/dto/vcn-bulk-job.dto'

const { Content } = Layout

/**
 * The two views are tabs rather than a switch on one grid.
 *
 * Each tab renders exactly one entity — Card Mode a card per row, Benefit Mode a
 * benefit per row — and later shows only that entity's tiles, which is what
 * makes "the tile's count equals the rows it filters" structural rather than
 * something to keep in sync. It also means the tabs share no filter or
 * pagination state: their filter sets genuinely differ (Card Mode has `inUse`
 * and the card's own status; Benefit Mode has the benefit's), so carrying state
 * across would smear one tab's meaning onto the other.
 */
enum VcnManagementTab {
  CARD = 'card',
  BENEFIT = 'benefit',
}

/**
 * `layouts/Page` hardcodes a 50px gap below the title and every page in the app
 * shares it, so it is narrowed here rather than there. This page carries five
 * metric tiles, a toolbar and a grid of 11k rows — the height matters more here
 * than the breathing room does.
 */
const CompactPageChrome = styled.div`
  .page-header {
    padding-bottom: 16px !important;
  }

  .ant-tabs-nav {
    margin-bottom: 8px;
  }
`

export default function VcnManagement() {
  const [activeTab, setActiveTab] = useState<VcnManagementTab>(
    VcnManagementTab.CARD
  )

  /* ----------------------------- Bulk job state -----------------------------
   * Owned here rather than in Benefit Mode, even though jobs are started from
   * there: a queued or running job outlives a tab switch, and the inactive pane
   * is unmounted. Polling it from inside that pane meant progress silently
   * stopped updating the moment an admin looked at Card Mode.
   */
  const [jobProgressOpen, setJobProgressOpen] = useState(false)
  const jobAutoOpenedIdRef = useRef<string | null>(null)
  const prevJobStateRef = useRef<string | null | undefined>(undefined)
  const invalidateVcnData = useInvalidateVcnData()

  const {
    job: bulkJob,
    loading: bulkJobLoading,
    error: bulkJobError,
    refresh: refreshBulkJob,
    isJobActive,
  } = useVcnBulkJobCurrent()

  // Surface an active job once per job id, whichever tab is open.
  useEffect(() => {
    if (bulkJobLoading) return
    if (
      !bulkJob ||
      (bulkJob.state !== VcnBulkJobState.QUEUED &&
        bulkJob.state !== VcnBulkJobState.RUNNING)
    )
      return
    if (jobAutoOpenedIdRef.current === bulkJob.jobId) return
    jobAutoOpenedIdRef.current = bulkJob.jobId
    setJobProgressOpen(true)
  }, [bulkJob, bulkJobLoading])

  // A job reaching a terminal state changes both tabs' data, so refresh
  // everything — the job can finish while the user is on either one.
  useEffect(() => {
    if (bulkJobLoading) return
    const prev = prevJobStateRef.current
    const curr = bulkJob?.state ?? null
    prevJobStateRef.current = curr
    const isNowTerminal =
      curr !== null &&
      curr !== VcnBulkJobState.QUEUED &&
      curr !== VcnBulkJobState.RUNNING
    const wasActiveOrFirstLoad =
      prev === undefined ||
      prev === VcnBulkJobState.QUEUED ||
      prev === VcnBulkJobState.RUNNING ||
      prev === VcnBulkJobState.TIMEOUT
    if (wasActiveOrFirstLoad && isNowTerminal) {
      invalidateVcnData()
    }
  }, [bulkJob?.state, bulkJobLoading, invalidateVcnData])

  const handleShowJobProgress = useCallback(() => {
    setJobProgressOpen(true)
  }, [])

  return (
    <PageWrapper>
      <CompactPageChrome>
        <Page
          title="VCN Management"
          // Full width rather than Page's default 1280px clamp: the card table
          // alone is 1020px of columns before the sidebar, and Application Engine
          // sets contentWidth="100%" for the same reason.
          maxWidth="100%"
          breadcrumb={
            <BreadcrumbNav
              items={[
                { title: 'Finance', path: FINANCE_PATH },
                {
                  title: 'VCN Management',
                  path: `${FINANCE_PATH}/vcn-management`,
                },
              ]}
            />
          }
        >
          <Layout style={{ backgroundColor: '#f6f9fc' }}>
            <Content
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                // No top padding: the page header above already provides the
                // separation, and this page needs the height for the grid.
                padding: '0 24px 24px',
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                flex: 1,
              }}
            >
              <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as VcnManagementTab)}
                // Each tab keeps its own state, so the inactive one is unmounted
                // rather than hidden — no background refetching for a view nobody
                // is looking at, and no stale selection carried across a switch.
                destroyInactiveTabPane
                items={[
                  {
                    key: VcnManagementTab.CARD,
                    label: 'VCN Cards',
                    children: (
                      <VcnManagementCardTab
                        bulkJob={bulkJob}
                        isJobActive={isJobActive}
                        refreshBulkJob={refreshBulkJob}
                        onShowJobProgress={handleShowJobProgress}
                      />
                    ),
                  },
                  {
                    key: VcnManagementTab.BENEFIT,
                    label: 'Benefits',
                    children: (
                      <VcnManagementBenefitTab
                        bulkJob={bulkJob}
                        isJobActive={isJobActive}
                        refreshBulkJob={refreshBulkJob}
                        onShowJobProgress={handleShowJobProgress}
                      />
                    ),
                  },
                ]}
              />
              {/* Outside the Tabs on purpose: a running job's progress must stay
                visible and polling regardless of which tab is open. */}
              <VcnManagementJobProgressModal
                open={jobProgressOpen}
                onClose={() => setJobProgressOpen(false)}
                job={bulkJob}
                loading={bulkJobLoading}
                error={bulkJobError}
                onRefresh={refreshBulkJob}
              />
            </Content>
          </Layout>
        </Page>
      </CompactPageChrome>
    </PageWrapper>
  )
}
